import { httpClient } from '../httpClient/httpClient';
import { PagedItems } from '@texas/types';
import { SimpleContact } from './contactsApi';
import { BaseSample, SampleReceiver } from './samplesApi';
import { ReceiverUser, ShipmentState } from './shipmentsApi';

export interface SampleIdsData {
  sampleIds: number[];
}

export interface AddSamplesToMultipleTransportRequests {
  data: { sampleIds: number[]; transportRequestId: number }[];
}

export interface TransportRequestShipment {
  id: number;
  trackingNumber: string;
  dispatchDate: string;
  courier: string;
  state: ShipmentState;
  receiverName: string;
  receiverBranch?: string;
  receiverCustomer?: string;
  receiverCustomerId?: number;
}

export interface TransportRequest {
  id: number;
  asapShipment: boolean;
  requestedDispatchDate: string;
  createdByUserId: number;
  createdByUserName: string;
  createdByUserEmail: string;
  receiverUser: ReceiverUser;
  sampleReceiver: SampleReceiver;
  branchId: number;
  branchIdentifier: string;
  note: string;
  samplesInTransport: number;
  shipment: TransportRequestShipment | null;
}

export interface CreateTransportRequest {
  requestDispatchDate: Date;
  asap: boolean;
  note: string;
  receiver: SampleReceiver;
  receiverUserId: number | null;
  receiverContactId: number | null;
}

export interface UpdateTransportRequest {
  requestDispatchDate: Date;
  asap: boolean;
  note: string;
}

interface CustomerSamplesTranports {
  customerName: string;
  projectNumber: string | null;
  customerId: number;
  contact: SimpleContact | null;
  samples: BaseSample[];
}

export interface SearchQuery {
  sortBy: string;
  sortDesc: boolean;
  page: number;
  pageSize: number;
  branchId: number | null;
  shipmentType: string | null;
  trackingNumber: string;
  customerId: number | null;
  includeDispatched: boolean;
  paOrItemCode: string;
}

export const transportRequestsApi = {
  filter: (params: SearchQuery) =>
    httpClient.get<PagedItems<TransportRequest>>(`transport-requests/filter`, {
      params,
    }),
  exportProductApprovals: (params: SearchQuery) =>
    httpClient.post<Blob>(`transport-requests/product-approvals/export`, {
      data: params,
      responseType: 'blob',
    }),
  toBrandFromSampleIds: (sampleIds: number[]) =>
    httpClient.get<CustomerSamplesTranports[]>(
      `transport-requests/brand/generate/sample-ids`,
      {
        params: { sampleIds },
      },
    ),
  getSamplesInTransportRequest: (transportRequestId: number) =>
    httpClient.get<BaseSample[]>(
      `transport-requests/${transportRequestId}/samples`,
    ),
  addSamplesToTransportRequest: (
    transportRequestId: number,
    sampleIds: number[],
  ) =>
    httpClient.post(`transport-requests/${transportRequestId}/samples`, {
      data: sampleIds,
    }),
  removeSamples: (data: SampleIdsData) =>
    httpClient.put(`transport-requests/samples/remove`, {
      data,
    }),
  addSamplesToMultipleTransportRequests: (
    data: AddSamplesToMultipleTransportRequests,
  ) =>
    httpClient.post(`transport-requests/multi/samples`, {
      data,
    }),
  upcomingFromMeToInternal: (toUserId: number) =>
    httpClient.get<TransportRequest[]>(
      `transport-requests/upcoming/internal/${toUserId}`,
    ),
  upcomingFromMeToContact: (toContactId: number) =>
    httpClient.get<TransportRequest[]>(
      `transport-requests/upcoming/external/${toContactId}`,
    ),
  create: (request: CreateTransportRequest) =>
    httpClient.post<number>(`transport-requests`, { data: request }),
  update: (id: number, request: UpdateTransportRequest) =>
    httpClient.put(`transport-requests/${id}`, { data: request }),
  remove: (id: number) => httpClient.delete(`transport-requests/${id}`),
  get: (id: number) =>
    httpClient.get<TransportRequest>(`transport-requests/${id}`),
};
